import axios from 'axios';
import { getStoredToken, clearStoredToken } from './authToken';

/**
 * SSOT for the axios API base.
 * Unset, blank, or whitespace-only REACT_APP_BACKEND_URL → same-origin `/api`
 * (nginx on pl.evonucleus.ch proxies that path to uvicorn).
 * A non-empty value is an origin only; this function appends `/api`.
 * Non-strings (webpack leaves the var undefined when it was unset at build time)
 * take the same `/api` path so the bundle never calls `undefined/api`.
 */
export function resolveApiBaseUrl(rawBackendUrl) {
  const backendUrl = typeof rawBackendUrl === 'string' ? rawBackendUrl.trim().replace(/\/$/, '') : '';
  return backendUrl ? `${backendUrl}/api` : '/api';
}

const API = resolveApiBaseUrl(process.env.REACT_APP_BACKEND_URL);

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

api.interceptors.request.use((config) => {
  const url = config.url || '';
  const requestId = createRequestId();
  config.headers = config.headers || {};
  config.headers['X-Request-ID'] = requestId;
  if (url === '/auth/login' || url.endsWith('/auth/login')) {
    return config;
  }
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || '';
    if (status === 401 && url !== '/auth/login' && !url.endsWith('/auth/login')) {
      clearStoredToken();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });

// Forecast (Phase 4+)
export const getForecastSummary = (params = {}) => api.get('/forecast/summary', { params });
export const getForecastAnnualTarget = (params = {}) => api.get('/forecast/annual-target', { params });
export const putForecastAnnualTarget = (body) => api.put('/forecast/annual-target', body);
export const getForecastHistory = (params = {}) => api.get('/forecast/history', { params });
export const getForecastTimeline = (params = {}) => api.get('/forecast/timeline', { params });
export const getForecastCashflow = (params = {}) => api.get('/forecast/cashflow', { params });
export const getForecastMonthlyPerformance = (params = {}) =>
  api.get('/forecast/monthly-performance', { params });
export const getReceivablesSummary = (params = {}) =>
  api.get('/ar/summary', { params });
export const getReceivablesByClient = (params = {}) =>
  api.get('/ar/by-client', { params });
export const getReceivablesByDeal = (params = {}) =>
  api.get('/ar/by-deal', { params });
export const getReceivablesByMonth = (params = {}) =>
  api.get('/ar/by-month', { params });
export const getReceivablesByProject = (params = {}) =>
  api.get('/ar/by-project', { params });
export const getReceivablesDetails = (params = {}) =>
  api.get('/ar/tranches', { params });
export const getBexioReconciliation = ({ refresh = false } = {}) =>
  api.get('/ar/bexio-reconciliation', { params: refresh ? { refresh: 1 } : {} });
/** POST: sync Bexio Paid into linked ledgers, then return fresh recon (mutations). */
export const syncBexioReconciliationPaid = () =>
  api.post('/ar/bexio-reconciliation/sync-paid');
export const cleanupBexioReconOrphans = () =>
  api.post('/ar/bexio-reconciliation/cleanup-orphans');
export const linkBexioReconInvoice = (dealId, invoiceId, data = {}) =>
  api.post('/ar/bexio-reconciliation/link-invoice', {
    deal_id: dealId,
    invoice_id: invoiceId,
    ...data,
  });
export const getDealLinkOptions = () => api.get('/deals/link-options');
export const getForecastBucketDeals = (view, bucket, filters = {}) =>
  api.get('/forecast/bucket-deals', { params: { view, bucket, ...filters } });

export const getForecastKpiExplain = (metric, params = {}) =>
  api.get('/forecast/kpi-explain', { params: { metric, ...params } });

// Master Price List
export const getMasterPriceListItems = (params = {}) => api.get('/master-price-list', { params });
export const getMasterPriceListItem = (id) => api.get(`/master-price-list/${encodeURIComponent(id)}`);
export const createMasterPriceListItem = (data) => api.post('/master-price-list', data);
export const updateMasterPriceListItem = (id, data) => api.put(`/master-price-list/${encodeURIComponent(id)}`, data);
export const deleteMasterPriceListItem = (id) => api.delete(`/master-price-list/${encodeURIComponent(id)}`);
export const duplicateMasterPriceListItem = (id) => api.post(`/master-price-list/${encodeURIComponent(id)}/duplicate`);
export const getMasterPriceListSummary = (params = {}) => api.get('/master-price-list/summary', { params });
export const importMasterPriceListToDeal = (dealId, data) =>
  api.post(`/deals/${encodeURIComponent(dealId)}/import-master-price-list`, data);

// Maintenance
export const getMaintenanceItems = (params = {}) => api.get('/maintenance', { params });
export const getMaintenanceItem = (id) => api.get(`/maintenance/${encodeURIComponent(id)}`);
export const createMaintenanceItem = (data) => api.post('/maintenance', data);
export const updateMaintenanceItem = (id, data) => api.put(`/maintenance/${encodeURIComponent(id)}`, data);
export const deleteMaintenanceItem = (id) => api.delete(`/maintenance/${encodeURIComponent(id)}`);
export const getMaintenanceSummary = () => api.get('/maintenance/summary');
export const getMaintenanceWebsiteStatus = (domains = []) =>
  api.post('/maintenance/website-status', { domains });
export const addMaintenancePayment = (id, data) =>
  api.post(`/maintenance/${encodeURIComponent(id)}/payments`, data);
export const renewMaintenanceItem = (id, data = {}) =>
  api.post(`/maintenance/${encodeURIComponent(id)}/renew`, data);
export const launchMaintenanceCampaign = (id) =>
  api.post(`/maintenance/${encodeURIComponent(id)}/launch-campaign`);
export const downloadMaintenanceInvoicePdf = (id, options = {}) =>
  api.get(`/maintenance/${encodeURIComponent(id)}/invoice-pdf`, {
    responseType: 'blob',
    params: options.disposition ? { disposition: options.disposition } : undefined,
  });

// Maintenance dripping (prep / invoice / after / overdue email sequences)
export const getMaintenanceDripSettings = () => api.get('/maintenance/drip/settings');
export const updateMaintenanceDripSettings = (data) => api.put('/maintenance/drip/settings', data);
export const getMaintenanceDripSends = (params = {}) => api.get('/maintenance/drip/sends', { params });
export const runMaintenanceDripNow = (params = {}) =>
  api.post('/maintenance/drip/run-now', null, { params });
export const drillMaintenanceDripNow = () => api.post('/maintenance/drip/drill');
export const previewMaintenanceDripEmail = (data) =>
  api.post('/maintenance/drip/preview', data);

// Notifications (generic SSOT-backed engine; first consumer: maintenance fee milestones)
export const getNotificationsUnreadCount = () => api.get('/notifications/unread-count');
export const getNotifications = (params = {}) => api.get('/notifications', { params });
export const patchNotification = (id, data) =>
  api.patch(`/notifications/${encodeURIComponent(id)}`, data);

// FX Rates
export const getFXRates = () => api.get('/fx-rates');

// bexio (contacts for deal CLIENT field; backend uses BEXIO_ACCESS_TOKEN)
export const getBexioContacts = (params = {}) =>
  api.get('/integrations/bexio/contacts', { params });
export const getBexioDealInvoices = (dealId) =>
  api.get(`/integrations/bexio/deals/${encodeURIComponent(dealId)}/invoices`);
/** Refresh path: list + auto-post Bexio Paid into issued ledger when engaged. */
export const syncBexioDealInvoicesPaid = (dealId) =>
  api.post(`/integrations/bexio/deals/${encodeURIComponent(dealId)}/invoices/sync-paid`);
export const getBexioMaintenanceInvoices = (itemId) =>
  api.get(`/integrations/bexio/maintenance/${encodeURIComponent(itemId)}/invoices`);
export const linkBexioMaintenanceInvoice = (itemId, invoiceId) =>
  api.post(`/integrations/bexio/maintenance/${encodeURIComponent(itemId)}/invoices/${encodeURIComponent(invoiceId)}/link`);
export const unlinkMaintenanceBexioInvoice = (itemId) =>
  api.post(`/maintenance/${encodeURIComponent(itemId)}/unlink-bexio-invoice`);
export const createBexioDealContact = (dealId, data = {}) =>
  api.post(`/integrations/bexio/deals/${encodeURIComponent(dealId)}/contact`, data);
export const issueBexioQuote = (dealId, data = {}) =>
  api.post(`/integrations/bexio/deals/${encodeURIComponent(dealId)}/quotes/issue`, data);
export const downloadBexioQuotePdf = (dealId, quoteId, options = {}) =>
  api.get(
    `/integrations/bexio/deals/${encodeURIComponent(dealId)}/quotes/${encodeURIComponent(quoteId)}/pdf`,
    {
      responseType: 'blob',
      params: options.disposition ? { disposition: options.disposition } : undefined,
    }
  );
/** Backend SSOT for quote-PDF VAT defaults (do not reimplement currency→VAT in UI). */
export const getDealQuotePdfOptions = (dealId) =>
  api.get(`/deals/${encodeURIComponent(dealId)}/quote-pdf-options`);
/** In-app branded quote PDF (net lines + VAT mode). Does not call Bexio. */
export const downloadDealQuotePdf = (dealId, options = {}) =>
  api.get(`/deals/${encodeURIComponent(dealId)}/quote-pdf`, {
    responseType: 'blob',
    params: {
      vat_mode: options.vatMode || 'auto',
      lang: options.lang || 'fr',
      mode: options.mode || 'line_items',
      ...(options.bulkLineText ? { bulk_line_text: options.bulkLineText } : {}),
      ...(options.disposition ? { disposition: options.disposition } : {}),
    },
  });
export const issueBexioInvoice = (dealId, invoiceId, data = {}) =>
  api.post(`/integrations/bexio/deals/${encodeURIComponent(dealId)}/invoices/${encodeURIComponent(invoiceId)}/issue`, data);
export const markBexioInvoicePaid = (dealId, invoiceId, data = {}) =>
  api.post(`/integrations/bexio/deals/${encodeURIComponent(dealId)}/invoices/${encodeURIComponent(invoiceId)}/paid`, data);

// Categories
export const getCategories = () => api.get('/categories');
export const createCategory = (name) => api.post('/categories', { name });
export const deleteCategory = (id) => api.delete(`/categories/${encodeURIComponent(id)}`);

// Vendors
export const getVendors = () => api.get('/vendors');
export const createVendor = (name) => api.post('/vendors', { name });
export const getVendorAggregation = () => api.get('/vendors/aggregation');
export const getVendorLineItems = (vendorName) => api.get(`/vendors/${encodeURIComponent(vendorName)}/line-items`);
export const getVendorDealsBreakdown = (vendorName) =>
  api.get(`/vendors/${encodeURIComponent(vendorName)}/deals-breakdown`);
export const getVendorPaymentsLog = (vendorName) =>
  api.get(`/vendors/${encodeURIComponent(vendorName)}/payments-log`);

export const getDealVendorPayments = (dealId) => api.get(`/deals/${encodeURIComponent(dealId)}/vendor-payments`);
export const addDealVendorPayment = (dealId, data) =>
  api.post(`/deals/${encodeURIComponent(dealId)}/vendor-payments`, data);
export const deleteDealVendorPayment = (dealId, paymentId) =>
  api.delete(`/deals/${encodeURIComponent(dealId)}/vendor-payments/${encodeURIComponent(paymentId)}`);

// Deals
export const getDeals = () => api.get('/deals');
export const getDeal = (id) => api.get(`/deals/${id}`);
export const createDeal = (data) => api.post('/deals', data);
export const createQuickDeal = (data) => api.post('/deals/quick', data);
export const updateDeal = (id, data) => api.put(`/deals/${id}`, data);
export const patchDeal = (id, data) => api.patch(`/deals/${id}`, data);
export const bulkLinkDeals = (dealIds, opts = {}) => {
  const body = { deal_ids: dealIds };
  const adopt = opts.adoptLinkGroupId;
  if (adopt != null && String(adopt).trim()) {
    body.link_group_id = String(adopt).trim().slice(0, 64);
  }
  return api.post('/deals/bulk-link', body);
};
export const deleteDeal = (id) => api.delete(`/deals/${id}`);
export const duplicateDeal = (id) => api.post(`/deals/${id}/duplicate`);
export const lockFX = (id) => api.post(`/deals/${id}/lock-fx`);
export const unlockFX = (id) => api.post(`/deals/${id}/unlock-fx`);
export const addDealNote = (dealId, data) => api.post(`/deals/${dealId}/notes`, data);
export const addDealPaymentLog = (dealId, data) => api.post(`/deals/${dealId}/payment-logs`, data);
export const deleteDealPaymentLog = (dealId, paymentId) => api.delete(`/deals/${dealId}/payment-logs/${paymentId}`);
export const generateDealTranches = (dealId) => api.post(`/ar/deals/${dealId}/tranches/generate`);
export const getDealTranches = (dealId) => api.get(`/ar/deals/${dealId}/tranches`);
export const getDealArPayments = (dealId) => api.get(`/ar/deals/${dealId}/payments`);
export const patchTranche = (trancheId, data) => api.patch(`/ar/tranches/${trancheId}`, data);
export const adjustIssuedTranche = (trancheId, data) => api.post(`/ar/tranches/${trancheId}/adjustments`, data);
export const deleteTranche = (trancheId) => api.delete(`/ar/tranches/${trancheId}`);
export const addTranchePayment = (trancheId, data) => api.post(`/ar/tranches/${trancheId}/payments`, data);
export const previewTrancheFx = (trancheId, data) => api.post(`/ar/tranches/${trancheId}/fx-preview`, data);
export const revalueTrancheFx = (trancheId, data) => api.post(`/ar/tranches/${trancheId}/fx-revalue`, data);
export const previewPaymentFx = (paymentId, data) => api.post(`/ar/payments/${paymentId}/fx-preview`, data);
export const revaluePaymentFx = (paymentId, data) => api.post(`/ar/payments/${paymentId}/fx-revalue`, data);
export const getDealFxAudit = (dealId) => api.get(`/ar/deals/${encodeURIComponent(dealId)}/fx-audit`);
export const voidArPayment = (paymentId, data = {}) => api.post(`/ar/payments/${paymentId}/void`, data);
export const deleteArPayment = (paymentId) => voidArPayment(paymentId, { reason: 'ui_delete_action' });

// Settings / Backups
export const getBackupSettings = () => api.get('/settings/backup');
export const updateBackupSettings = (data) => api.put('/settings/backup', data);
export const runBackupNow = () => api.post('/settings/backup/run-now');
export const getBackupLogs = (params = {}) => api.get('/settings/backup/logs', { params });
export const getBackupSnapshots = (params = {}) => api.get('/settings/backup/snapshots', { params });
export const restoreBackupSnapshot = (snapshotId, data) =>
  api.post(`/settings/backup/restore/${encodeURIComponent(snapshotId)}`, data);

// Line Items
export const addLineItem = (dealId, data) => api.post(`/deals/${dealId}/line-items`, data);
export const updateLineItem = (dealId, itemId, data) => api.put(`/deals/${dealId}/line-items/${itemId}`, data);
export const deleteLineItem = (dealId, itemId) => api.delete(`/deals/${dealId}/line-items/${itemId}`);
export const duplicateLineItem = (dealId, itemId) => api.post(`/deals/${dealId}/line-items/${itemId}/duplicate`);

// AI deal advisor (OpenAI on server — requires OPENAI_API_KEY)
export const analyzeDealAdvisor = (dealId, focus = 'all') =>
  api.post(`/deals/${dealId}/advisor/analyze`, {}, { params: { focus } });

// Bulk Line Items (Paste Assistant)
export const bulkAddLineItems = (dealId, data) => api.post(`/deals/${dealId}/bulk-line-items`, data);

// Scenarios
export const createScenario = (dealId, data) => api.post(`/deals/${dealId}/scenarios`, data);
export const duplicateScenario = (dealId, scenarioId, data) => api.post(`/deals/${dealId}/scenarios/${scenarioId}/duplicate`, data);
export const activateScenario = (dealId, scenarioId) => api.post(`/deals/${dealId}/scenarios/${scenarioId}/activate`);
export const deleteScenario = (dealId, scenarioId) => api.delete(`/deals/${dealId}/scenarios/${scenarioId}`);
export const renameScenario = (dealId, scenarioId, data) => api.put(`/deals/${dealId}/scenarios/${scenarioId}`, data);

export default api;
